import {
  getIntegrationDisplayStatus,
  normalizeMCPUrl,
  type CachedMCPServer,
  type MCPCatalogEntry,
} from '@littlebird/mcp';
import { integrationStatusPriority } from '@littlebird/ui-utils';
import { useCallback, useMemo } from 'react';

import { FEATURES, useFeature } from '@/features/feature-flags';
import {
  useMCPCatalogQuery,
  useMCPServersQuery,
} from '@/features/mcp/mcp.query';

/**
 * A connected MCP server and all of its accounts (rows sharing a `slug`).
 * `representative` is the most-actionable account, used for catalog matching
 * and the collapsed-row display.
 */
export type MCPServerGroup = {
  slug: string;
  accounts: CachedMCPServer[];
  representative: CachedMCPServer;
};

function pickRepresentative(accounts: CachedMCPServer[]): CachedMCPServer {
  return [...accounts].sort(
    (a, b) =>
      integrationStatusPriority[getIntegrationDisplayStatus(a)] -
      integrationStatusPriority[getIntegrationDisplayStatus(b)]
  )[0];
}

/** Group MCP server rows into one entry per server (by `slug`), each with its accounts. */
export function groupMCPServersBySlug(
  servers: CachedMCPServer[]
): MCPServerGroup[] {
  const bySlug = new Map<string, CachedMCPServer[]>();
  for (const server of servers) {
    const list = bySlug.get(server.slug);
    if (list) list.push(server);
    else bySlug.set(server.slug, [server]);
  }
  return [...bySlug.entries()].map(([slug, accounts]) => ({
    slug,
    accounts: [...accounts].sort((a, b) =>
      a.account_label.localeCompare(b.account_label)
    ),
    representative: pickRepresentative(accounts),
  }));
}

export type CatalogEntryWithGroup = {
  entry: MCPCatalogEntry;
  group: MCPServerGroup | null;
};

/**
 * Single source for MCP integration view state: fetches servers + catalog
 * (gated on the MCP feature flag), groups servers by `slug` (multi-account),
 * and joins them to catalog entries. Shared by the settings page and the
 * add-integrations modal.
 */
export function useMCPIntegrationState() {
  const hasFeature = useFeature();
  const mcpEnabled = hasFeature(FEATURES.MCP_INTEGRATIONS);

  const {
    data: catalogEntries,
    error: catalogError,
    refetch: refetchCatalog,
  } = useMCPCatalogQuery(mcpEnabled);
  const {
    data: mcpServers,
    error: serversError,
    refetch: refetchServers,
  } = useMCPServersQuery(mcpEnabled);

  // Only an error when there's nothing to render — a failed background
  // refetch with cached data keeps showing the data
  const loadError = mcpEnabled
    ? (!mcpServers && serversError) || (!catalogEntries && catalogError) || null
    : null;

  const refetch = useCallback(() => {
    refetchServers();
    refetchCatalog();
  }, [refetchServers, refetchCatalog]);

  const groups = useMemo(
    () => groupMCPServersBySlug(mcpServers ?? []),
    [mcpServers]
  );

  // Every catalog entry, joined with its server group when connected. Match on
  // ANY account's normalized URL, not just the representative's: per-instance
  // accounts can carry different URLs, and the representative changes with
  // status — neither should move a catalog integration into the custom section.
  const catalogWithServers = useMemo<CatalogEntryWithGroup[]>(() => {
    if (!mcpEnabled || !catalogEntries) return [];
    return catalogEntries.map((entry) => ({
      entry,
      group:
        groups.find((g) =>
          g.accounts.some(
            (account) =>
              normalizeMCPUrl(account.url) === normalizeMCPUrl(entry.url)
          )
        ) ?? null,
    }));
  }, [mcpEnabled, catalogEntries, groups]);

  const connectedCatalogServers = useMemo(
    () =>
      catalogWithServers.flatMap(({ entry, group }) =>
        group ? [{ entry, group }] : []
      ),
    [catalogWithServers]
  );

  // Custom servers: groups where no account's URL is in the catalog (the exact
  // complement of the any-account catalog match above).
  const customMCPServers = useMemo(() => {
    if (!mcpEnabled) return [];
    const catalogUrls = new Set(
      catalogEntries?.map((entry) => normalizeMCPUrl(entry.url))
    );
    return groups
      .filter(
        (g) =>
          !g.accounts.some((account) =>
            catalogUrls.has(normalizeMCPUrl(account.url))
          )
      )
      .sort((a, b) =>
        a.representative.name.localeCompare(b.representative.name)
      );
  }, [mcpEnabled, groups, catalogEntries]);

  return {
    mcpEnabled,
    catalogWithServers,
    connectedCatalogServers,
    customMCPServers,
    isLoading: mcpEnabled && !loadError && (!catalogEntries || !mcpServers),
    loadError,
    refetch,
  };
}
